import { ColorDefinition } from "@/types/colors";
import i18n from "@i18n/i18n";
import { Typography, useTheme } from "@mui/material";
import Box from "@mui/material/Box";
import _ from "lodash";
import { observer } from "mobx-react";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef } from "react";
import Chart, { GoogleChartWrapper, GoogleChartWrapperChartType, ReactGoogleChartEvent } from "react-google-charts";
import useState from "react-usestateref";

/** Pixels added to each side of a By-Model overlay line beyond the bar group span. */
const OVERLAY_SIDE_EXTENSION_PX = 14;

/**
 * Describes a horizontal reference line to overlay on the chart.
 * When `barIndices` is undefined the line spans the full chart width (e.g. WholeBuilding benchmark).
 * When `barIndices` is provided the line spans only the specified bars (e.g. per-model-type benchmark).
 */
export interface ChartOverlay {
  label: string;
  value: number;
  color: string;
  lineWidth?: number;
  lineDashStyle?: number[];
  barIndices?: number[];
  shortLabel?: string;
  /** Model type name displayed on a separate line in the popup card (By Model mode only). */
  typeLabel?: string;
  /** Unit label displayed alongside the value in the popup card (e.g. "kgCO₂e/m²"). */
  unit?: string;
}

interface OverlayLine {
  label: string;
  value: number;
  color: string;
  y: number;
  left: number;
  width: number;
  lineWidth: number;
  dash: number;
  gap: number;
  shortLabel?: string;
  typeLabel?: string;
  unit?: string;
  barIndices?: number[];
}

/** A group of OverlayLines sharing the same y-position and scope (collision = both values equal). */
interface RenderGroup {
  lines: OverlayLine[];
  isCollision: boolean;
  y: number;
  left: number;
  width: number;
}

export interface BarChartProps {
  id: string;
  width?: number;
  height?: number;
  data: any;
  colors?: ColorDefinition[];
  chartOptions?: any;
  slantedText: boolean;
  chartType?: GoogleChartWrapperChartType;
  overlays?: ChartOverlay[];
  onSelect: (rowIndex?: number, legend?: string | undefined) => void;
}

export type BarChartApi = {
  setSelection: ({ rowIndex, legends }: { rowIndex?: number; legends?: string | string[] | undefined }) => void;
};

const BarChart = observer(
  forwardRef((props: BarChartProps, ref: any) => {
    const theme = useTheme();
    const [key, setKey] = useState(false);
    const [chartWrapper, setChartWrapper] = useState<GoogleChartWrapper | null>(null);
    const [selectionModel, setSelectionModel] = useState<number[] | null>(null);
    const [prevSelectionModel, setPrevSelectionModel, prevSelectionModelRef] = useState<any>();
    const [overlayLines, setOverlayLines] = useState<OverlayLine[]>([]);
    const [expandedGroups, setExpandedGroups] = useState<Set<number>>(new Set());
    const overlaysRef = useRef<ChartOverlay[]>([]);
    overlaysRef.current = props.overlays ?? [];

    useImperativeHandle(ref, () => ({
      setSelection: ({ rowIndex, legends }: { rowIndex?: number; legends?: string | string[] | undefined }) => {
        updateChartSelection({ rowIndex, legends });
      },
    }));

    useEffect(() => {
      if (chartWrapper && selectionModel) {
        const newSelections: { row?: any; column?: any }[] = [];
        if (props.chartType === "BarChart") {
          newSelections.push({ row: selectionModel[0], column: 1 });
        } else {
          for (let i = 0; i < props.data.length - 1; i++) {
            selectionModel.forEach((column) => {
              newSelections.push({ row: i, column });
            });
          }
        }
        const chart: any = chartWrapper.getChart();
        const selections = chart.getSelection();
        if (!_.isEqual(selections, newSelections)) {
          chart.setSelection(newSelections);
        }
      }
    }, [chartWrapper, selectionModel]);

    // Reset all open popup cards when overlays are recomputed (unit/mode/data change).
    useEffect(() => {
      setExpandedGroups(new Set());
    }, [overlayLines]);

    const handleOnSelect = (rowIndex: number, legend: string | undefined) => {
      updateChartSelection({ legends: legend });
      props.onSelect(rowIndex, legend);
    };

    const updateChartSelection = ({ rowIndex, legends }: { rowIndex?: number; legends?: string | string[] | undefined }) => {
      if (rowIndex !== undefined) {
        setSelectionModel([rowIndex + 1]);
      } else {
        if (!Array.isArray(legends)) {
          legends = legends ? [legends] : [];
        }
        const columns = legends.map((legend) => props.data[0]?.indexOf(legend)) || [];
        setSelectionModel(columns);
      }
    };

    // ── Overlay line positioning via Google Charts layout API ─────────────────
    const computeOverlays = useCallback((wrapper: GoogleChartWrapper) => {
      const items = overlaysRef.current;
      if (!items.length || !wrapper) {
        setOverlayLines((prev) => (prev.length ? [] : prev));
        return;
      }

      try {
        const chart: any = wrapper.getChart();
        if (!chart?.getChartLayoutInterface) return;

        const cli = chart.getChartLayoutInterface();
        const area = cli.getChartAreaBoundingBox();
        const lines: OverlayLine[] = [];

        items.forEach((overlay) => {
          const y = cli.getYLocation(overlay.value);
          if (y < area.top || y > area.top + area.height) return;

          let left = area.left;
          let width = area.width;

          if (overlay.barIndices?.length) {
            try {
              const first = overlay.barIndices[0];
              const last = overlay.barIndices[overlay.barIndices.length - 1];
              const firstBox = cli.getBoundingBox(`bar#0#${first}`);
              const lastBox = cli.getBoundingBox(`bar#0#${last}`);
              if (firstBox && lastBox) {
                // Extend the line beyond the bar group on each side so it is
                // visually distinct from the bars in "Breakdown by Model" mode.
                const rawLeft = firstBox.left - OVERLAY_SIDE_EXTENSION_PX;
                const rawRight = lastBox.left + lastBox.width + OVERLAY_SIDE_EXTENSION_PX;
                left = Math.max(area.left, rawLeft);
                width = Math.min(area.left + area.width, rawRight) - left;
              }
            } catch {
              /* fallback to full width */
            }
          }

          const [dash, gap] = overlay.lineDashStyle ?? [8, 4];
          lines.push({
            label: overlay.label,
            value: overlay.value,
            color: overlay.color,
            y,
            left,
            width,
            lineWidth: overlay.lineWidth ?? 4,
            dash,
            gap,
            shortLabel: overlay.shortLabel,
            typeLabel: overlay.typeLabel,
            unit: overlay.unit,
            barIndices: overlay.barIndices,
          });
        });

        setOverlayLines((prev) => (JSON.stringify(prev) === JSON.stringify(lines) ? prev : lines));
      } catch {
        /* chart layout interface unavailable */
      }
    }, []);

    useEffect(() => {
      if (chartWrapper) computeOverlays(chartWrapper);
    }, [props.overlays, chartWrapper]);

    // ── Chart options ────────────────────────────────────────────────────────
    let chartOptions: any = {
      titleTextStyle: { color: theme.palette.text.primary },
      annotations: { textStyle: { color: theme.palette.text.primary } },
      hAxis: {
        title: i18n.t("barchart.title.hAxis"),
        minValue: 0,
        slantedText: props.slantedText,
        textStyle: { color: theme.palette.text.primary },
        titleTextStyle: { color: theme.palette.text.primary },
      },
      vAxis: {
        title: i18n.t("barchart.title.vAxis"),
        textStyle: { color: theme.palette.text.primary },
        titleTextStyle: { color: theme.palette.text.primary },
      },
      colors: [] as string[],
      legend: {
        position: "none",
        maxLines: 10,
        textStyle: { fontSize: 12, color: theme.palette.text.primary },
      },
      isStacked: true,
      chartArea: {
        top: 60,
        backgroundColor: { fill: theme.palette.background.default },
      },
      backgroundColor: { fill: theme.palette.background.default },
    };

    const selectCallback = ({ chartWrapper }: { chartWrapper: GoogleChartWrapper | null }) => {
      setChartWrapper(chartWrapper);
      if (!chartWrapper) return;

      const chart: any = chartWrapper.getChart();
      let selection = chart.getSelection();
      if (selection.at(0)) {
        setPrevSelectionModel(selection);
      } else if (prevSelectionModelRef.current) {
        chart.setSelection(prevSelectionModelRef.current);
        selection = prevSelectionModelRef.current;
      }

      if (!_.isNil(selection.at(0))) {
        const dataTable = chartWrapper.getDataTable();
        const row = selection.at(0)?.row;
        const column = selection.at(0)?.column - 1;
        const legend = dataTable?.getColumnLabel(selection.at(0)?.column);
        const rowIndex = props.chartType === "BarChart" ? row : column;
        handleOnSelect(rowIndex, legend);
      }
    };

    const chartEvents: ReactGoogleChartEvent[] = [
      { eventName: "select", callback: selectCallback },
      {
        eventName: "ready",
        callback: ({ chartWrapper: cw }: { chartWrapper: GoogleChartWrapper | null }) => {
          setChartWrapper(cw);
          if (cw) computeOverlays(cw);
        },
      },
    ];

    chartOptions = _.merge(chartOptions, props.chartOptions);

    if (props.colors) {
      chartOptions.colors = props.colors.map((c) => c.hex);
    }

    useEffect(() => {
      setKey((prevKey) => !prevKey);
    }, [props?.width, props?.height]);

    const chartType = props.chartType || "ColumnChart";
   
    // Two lines with the same values - y axis value (±2px) and the same barIndices scope are
    // "colliding" — benchmark and target values are equal. They are rendered as
    // as two different lines with a combined popup card
    const renderGroups = useMemo<RenderGroup[]>(() => {
      const groups: RenderGroup[] = [];
      const used = new Set<number>();

      overlayLines.forEach((line, i) => {
        if (used.has(i)) return;
        used.add(i);

        const scopeKey = JSON.stringify([...(line.barIndices ?? [])].sort((a, b) => a - b));
        const group: RenderGroup = {
          lines: [line],
          isCollision: false,
          y: line.y,
          left: line.left,
          width: line.width,
        };

        overlayLines.forEach((other, j) => {
          if (j <= i || used.has(j)) return;
          const otherScopeKey = JSON.stringify([...(other.barIndices ?? [])].sort((a, b) => a - b));
          if (Math.abs(line.y - other.y) < 2 && scopeKey === otherScopeKey) {
            group.lines.push(other);
            group.isCollision = true;
            used.add(j);
          }
        });

        groups.push(group);
      });

      return groups;
    }, [overlayLines]);

    const toggleGroup = (groupIdx: number, e: { stopPropagation: () => void }) => {
      e.stopPropagation();
      setExpandedGroups((prev) => {
        const next = new Set(prev);
        next.has(groupIdx) ? next.delete(groupIdx) : next.add(groupIdx);
        return next;
      });
    };

    return (
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: props?.width,
          bottom: props?.height,
          '& [stroke-width="1"]': { strokeWidth: 2, stroke: theme.palette.text.primary },
        }}
      >
        <Chart
          key={`${props.id}-${key}-${chartType}`}
          width={props.width}
          height={props.height}
          chartType={chartType}
          loader={<div>{i18n.t("barchart.title.chart-progress")}</div>}
          data={props.data}
          options={chartOptions}
          chartEvents={chartEvents}
        />

        {renderGroups.map((group, groupIdx) => {
          const isExpanded = expandedGroups.has(groupIdx);
          const firstLine = group.lines[0];
          // Center the popup horizontally over the line; clamp to avoid left overflow.
          const popupLeft = Math.max(4, group.left + group.width / 2 - 100);

          return (
            <Box key={groupIdx}>
              {/* ── Overlay line hit-area (click to toggle popup) ── */}
              <Box
                onClick={(e) => toggleGroup(groupIdx, e)}
                sx={{
                  position: "absolute",
                  top: `${group.y}px`,
                  left: `${group.left}px`,
                  width: `${group.width}px`,
                  marginTop: "-18px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  cursor: "pointer",
                  zIndex: 10,
                }}
              >
                {/* Short label(s) above the dashed line */}
                {group.isCollision ? (
                  <Box sx={{ display: "flex", gap: "6px", pointerEvents: "none", mb: "2px" }}>
                    {group.lines.map((line, li) => (
                      <Typography
                        key={li}
                        sx={{
                          fontSize: "10px",
                          lineHeight: 1,
                          color: line.color,
                          whiteSpace: "nowrap",
                          fontWeight: 600,
                        }}
                      >
                        {line.shortLabel}
                      </Typography>
                    ))}
                  </Box>
                ) : (
                  firstLine.shortLabel && (
                    <Typography
                      sx={{
                        fontSize: "10px",
                        lineHeight: 1,
                        color: "#ffffff",
                        whiteSpace: "nowrap",
                        fontWeight: 600,
                        pointerEvents: "none",
                        mb: "2px",
                      }}
                    >
                      {firstLine.shortLabel}
                    </Typography>
                  )
                )}

                {/* Dashed line(s): dual-band for collision, single for normal */}
                {group.isCollision ? (
                  group.lines.map((line, li) => (
                    <Box
                      key={li}
                      sx={{
                        width: "100%",
                        height: `${line.lineWidth / 2}px`,
                        background: `repeating-linear-gradient(to right, ${line.color} 0px, ${line.color} ${line.dash}px, transparent ${line.dash}px, transparent ${line.dash + line.gap}px)`,
                      }}
                    />
                  ))
                ) : (
                  <Box
                    sx={{
                      width: "100%",
                      height: `${firstLine.lineWidth}px`,
                      background: `repeating-linear-gradient(to right, ${firstLine.color} 0px, ${firstLine.color} ${firstLine.dash}px, transparent ${firstLine.dash}px, transparent ${firstLine.dash + firstLine.gap}px)`,
                    }}
                  />
                )}
              </Box>

              {/* ── Persistent popup card — visible on click, hidden on second click ── */}
              {isExpanded && (
                <Box
                  sx={{
                    position: "absolute",
                    top: `${group.y - 22}px`,
                    left: `${popupLeft}px`,
                    transform: "translateY(-100%)",
                    width: "210px",
                    backgroundColor: "rgba(40, 40, 40, 0.95)",
                    borderRadius: "6px",
                    padding: "8px 12px",
                    zIndex: 20,
                    pointerEvents: "none",
                    // Downward-pointing arrow connecting the popup to the line below
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      bottom: "-7px",
                      left: "50%",
                      transform: "translateX(-50%)",
                      borderLeft: "7px solid transparent",
                      borderRight: "7px solid transparent",
                      borderTop: "7px solid rgba(40, 40, 40, 0.95)",
                    },
                  }}
                >
                  {group.lines.map((line, li) => (
                    <Box
                      key={li}
                      sx={li > 0 ? { mt: "6px", pt: "6px", borderTop: "1px solid rgba(255,255,255,0.15)" } : undefined}
                    >
                      {/* Show the overlay type (Benchmark / Target) in its own color when both are present */}
                      {group.isCollision && (
                        <Typography sx={{ fontSize: "11px", fontWeight: 700, color: line.color, lineHeight: 1.5 }}>
                          {line.shortLabel}
                        </Typography>
                      )}
                      <Typography sx={{ fontSize: "11px", color: "#ffffff", lineHeight: 1.5 }}>
                        {`Value: ${line.value.toLocaleString()} ${line.unit ?? "kgCO\u2082e/m\u00B2"}`}
                      </Typography>
                      {line.typeLabel && (
                        <Typography sx={{ fontSize: "11px", color: "#cccccc", lineHeight: 1.5 }}>
                          {`Model Type: ${line.typeLabel}`}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          );
        })}
      </Box>
    );
  }),
);

export default BarChart;
